import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ContactService, UserTicket } from '../services/contact.service';
import { AuthService } from '../services/auth.service';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';

@Component({
  selector: 'app-tickets',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, FooterComponent],
  templateUrl: './tickets.component.html',
  styleUrls: ['./tickets.component.css'],
})
export class TicketsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  tickets: UserTicket[] = [];
  isLoading = false;
  errorMessage = '';
  filteredTickets: UserTicket[] = [];
  selectedFilter = 'all';
  searchTerm = '';

  filterOptions = [
    { value: 'all', label: 'All Tickets', count: 0 },
    { value: 'pending', label: 'Pending', count: 0 },
    { value: 'in_progress', label: 'In Progress', count: 0 },
    { value: 'resolved', label: 'Resolved', count: 0 },
    { value: 'closed', label: 'Closed', count: 0 },
  ];

  constructor(
    private contactService: ContactService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadTickets();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadTickets(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.contactService
      .getUserTickets()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tickets) => {
          this.tickets = tickets;
          this.updateFilterCounts();
          this.applyFilters();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading tickets:', error);
          this.errorMessage = error.message || 'Failed to load tickets';
          this.isLoading = false;
        },
      });
  }

  updateFilterCounts(): void {
    this.filterOptions.forEach((option) => {
      if (option.value === 'all') {
        option.count = this.tickets.length;
      } else {
        option.count = this.tickets.filter(
          (ticket) => ticket.status === option.value
        ).length;
      }
    });
  }

  applyFilters(): void {
    let filtered = this.tickets;

    if (this.selectedFilter !== 'all') {
      filtered = filtered.filter(
        (ticket) => ticket.status === this.selectedFilter
      );
    }

    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (ticket) =>
          ticket.subject?.toLowerCase().includes(searchLower) ||
          ticket.message.toLowerCase().includes(searchLower) ||
          ticket.id.toLowerCase().includes(searchLower)
      );
    }

    this.filteredTickets = filtered;
  }

  onFilterChange(filter: string): void {
    this.selectedFilter = filter;
    this.applyFilters();
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.applyFilters();
  }

  viewTicket(ticket: UserTicket): void {
    this.router.navigate(['/tickets', ticket.id]);
  }

  createNewTicket(): void {
    this.router.navigate(['/contact-us']);
  }

  getStatusClass(status: string): string {
    return this.contactService.getStatusClass(status);
  }

  getPriorityClass(priority: string): string {
    return this.contactService.getPriorityClass(priority);
  }

  formatStatus(status: string): string {
    return this.contactService.formatStatus(status);
  }

  formatPriority(priority: string): string {
    return this.contactService.formatPriority(priority);
  }

  formatDate(dateString: string): string {
    return this.contactService.formatTicketDate(dateString);
  }

  hasNewAdminResponse(ticket: UserTicket): boolean {
    return this.contactService.hasNewAdminResponse(ticket);
  }

  getTicketIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return '⏳';
      case 'in_progress':
        return '🔄';
      case 'resolved':
        return '✅';
      case 'closed':
        return '🔒';
      default:
        return '📝';
    }
  }

  getResponseCountText(count: number): string {
    if (count === 0) return 'No responses';
    if (count === 1) return '1 response';
    return `${count} responses`;
  }

  clearErrorMessage(): void {
    this.errorMessage = '';
  }
}
